import React from 'react';
import { CancerConfig } from '../configs/cancerConfig';
import useCancerConfig from './useCancerConfig';
import useConfig, { ConfigTypes } from './useConfig';
import useConfigurationStore, {
  ConfigurationStoreProps,
  ConfigurationStoreValues,
  StoreConfigurationTypes
} from '../stores/configurationStore';

interface GetCancerTypeGuidFromLocationProps {
	locationData: CancerConfig[] | undefined;
	cancerLocationId: string;
}

export function GetCancerTypeGuidFromLocation({
  locationData,
  cancerLocationId
}: GetCancerTypeGuidFromLocationProps) {
  return locationData?.find((type) =>
    type.locations.some((location) => location.value === cancerLocationId)
  )?.value;
}

export function useQueryAndStoreConfig(
  store: ConfigurationStoreProps,
  key: ConfigTypes,
  enable: boolean
) {
  const { isLoading, isFetching } = useConfig(key, {
    onSuccess: (data) => {
      store[key] === undefined && store.setConfig(key, data);
    },
    enabled: store[key] === undefined && enable
  });

  return {
    isLoading: isLoading || isFetching
  };
}

export function useQueryAndStoreCancerConfig(store: ConfigurationStoreProps, enable: boolean) {
  const { isLoading, isFetching } = useCancerConfig({
    onSuccess: (data) => {
      store['cancer'] === undefined && store.setConfig('cancer', data);
    },
    enabled: store['cancer'] === undefined && enable
  });

  return {
    isLoading: isLoading || isFetching
  };
}

export function usePostCaseConfigs(keys: StoreConfigurationTypes[]) {
  const store = useConfigurationStore();

  const { isLoading: isCancerLoading } = useQueryAndStoreCancerConfig(
    store,
    keys.some((x) => x === 'cancer')
  );
  const { isLoading: isBehaviorLoading } = useQueryAndStoreConfig(
    store,
    'behavior',
    keys.some((x) => x === 'behavior')
  );
  const { isLoading: isEthnicitiesLoading } = useQueryAndStoreConfig(
    store,
    'ethnicities',
    keys.some((x) => x === 'ethnicities')
  );
  const { isLoading: isRaceLoading } = useQueryAndStoreConfig(
    store,
    'race',
    keys.some((x) => x === 'race')
  );
  const { isLoading: isSettingLoading } = useQueryAndStoreConfig(
    store,
    'setting',
    keys.some((x) => x === 'setting')
  );
  const { isLoading: isMenopausalLoading } = useQueryAndStoreConfig(
    store,
    'menopausal',
    keys.some((x) => x === 'menopausal')
  );
  const { isLoading: isRecurrenceLoading } = useQueryAndStoreConfig(
    store,
    'recurrence',
    keys.some((x) => x === 'recurrence')
  );
  const { isLoading: isRecurrenceAreasLoading } = useQueryAndStoreConfig(
    store,
    'recurrence_areas',
    keys.some((x) => x === 'recurrence_areas')
  );
  const { isLoading: isMetastasisAreasLoading } = useQueryAndStoreConfig(
    store,
    'areas_of_metastasis',
    keys.some((x) => x === 'areas_of_metastasis')
  );
  const { isLoading: isSexLoading } = useQueryAndStoreConfig(
    store,
    'sex',
    keys.some((x) => x === 'sex')
  );
  const { isLoading: isEventHistoryStatusLoading } = useQueryAndStoreConfig(
    store,
    'event_history_status',
    keys.some((x) => x === 'event_history_status')
  );
  const { isLoading: isPanelTypeLoading } = useQueryAndStoreConfig(
    store,
    'panel_type',
    keys.some((x) => x === 'panel_type')
  );
  const { isLoading: isTreatmentHistoryStatusLoading } = useQueryAndStoreConfig(
    store,
    'treatment_history_status',
    keys.some((x) => x === 'treatment_history_status')
  );

  const isLoading =
		isCancerLoading ||
		isBehaviorLoading ||
		isEthnicitiesLoading ||
		isRaceLoading ||
		isSettingLoading ||
		isMenopausalLoading ||
		isRecurrenceLoading ||
		isRecurrenceAreasLoading ||
		isMetastasisAreasLoading ||
		isSexLoading ||
		isEventHistoryStatusLoading ||
		isPanelTypeLoading ||
		isTreatmentHistoryStatusLoading;

  return React.useMemo(() => {
    if (isLoading) {
      return null;
    } else {
      const results = {} as ConfigurationStoreValues;
      return Object.assign(results, Object.fromEntries(keys.map((key) => [key, store[key]])));
    }
  }, [isLoading, ...keys.map((key) => store[key])]);
}
